import React, { useReducer } from "react";

const diceOptions = [6, 10, 20];

const initialState = {
  characters: [],
  diceSides: 20,
  currentTurnIndex: null,
};

function clampTurnIndex(turnIndex, length) {
  if (length <= 0) return null;
  if (turnIndex === null) return null;
  if (turnIndex < 0) return 0;
  if (turnIndex > length - 1) return length - 1;
  return turnIndex;
}

function initiativeReducer(state, action) {
  switch (action.type) {
    case "SET_DICE_SIDES": {
      return { ...state, diceSides: action.diceSides };
    }

    case "ADD_CHARACTER": {
      const nextChars = [
        ...state.characters,
        { id: crypto.randomUUID(), name: "", bonus: 0, roll: null },
      ];
      return {
        ...state,
        characters: nextChars,
        currentTurnIndex: clampTurnIndex(state.currentTurnIndex, nextChars.length),
      };
    }

    case "UPDATE_CHARACTER": {
      const { id, key, value } = action;
      const nextChars = state.characters.map((c) => {
        if (c.id !== id) return c;
        return { ...c, [key]: value };
      });
      return { ...state, characters: nextChars };
    }

    case "REMOVE_CHARACTER": {
      const { id } = action;

      const idx = state.characters.findIndex((c) => c.id === id);
      if (idx === -1) return state;

      const nextChars = state.characters.filter((c) => c.id !== id);

      let nextTurn = state.currentTurnIndex;
      if (nextTurn !== null) {
        if (idx < nextTurn) nextTurn = nextTurn - 1;
        else if (idx === nextTurn) {
          // removed the active entry
          nextTurn = nextChars.length ? Math.min(nextTurn, nextChars.length - 1) : null;
        }
      }

      return {
        ...state,
        characters: nextChars,
        currentTurnIndex: clampTurnIndex(nextTurn, nextChars.length),
      };
    }

    case "APPLY_ROLLS": {
      // action.characters is already rolled + sorted by the caller
      const nextChars = action.characters;
      return {
        ...state,
        characters: nextChars,
        currentTurnIndex: nextChars.length ? 0 : null,
      };
    }

    case "NEXT_TURN": {
      const len = state.characters.length;
      if (len <= 0) return { ...state, currentTurnIndex: null };

      const cur = state.currentTurnIndex;
      const next = cur === null || cur >= len - 1 ? 0 : cur + 1;
      return { ...state, currentTurnIndex: next };
    }

    case "RESET_TURN": {
      return { ...state, currentTurnIndex: null };
    }

    default:
      return state;
  }
}

export default function InitiativeTracker() {
  const [state, dispatch] = useReducer(initiativeReducer, initialState);
  const { characters, diceSides, currentTurnIndex } = state;

  const addCharacter = () => dispatch({ type: "ADD_CHARACTER" });

  const updateCharacter = (id, key, rawValue) => {
    const value =
      key === "bonus" ? (parseInt(rawValue, 10) || 0) : rawValue;

    dispatch({ type: "UPDATE_CHARACTER", id, key, value });
  };

  const removeCharacter = (id) => dispatch({ type: "REMOVE_CHARACTER", id });

  const rollInitiative = () => {
    // Roll outside reducer to keep reducer pure.
    const rolledSorted = characters
      .map((c) => ({
        ...c,
        roll: Math.floor(Math.random() * diceSides) + 1 + (c.bonus || 0),
      }))
      .sort((a, b) => {
        // primary: roll desc
        if ((b.roll ?? -Infinity) !== (a.roll ?? -Infinity)) return (b.roll ?? -Infinity) - (a.roll ?? -Infinity);
        // secondary: bonus desc (optional nice tie-break)
        if ((b.bonus ?? 0) !== (a.bonus ?? 0)) return (b.bonus ?? 0) - (a.bonus ?? 0);
        // tertiary: name asc
        return String(a.name || "").localeCompare(String(b.name || ""));
      });

    dispatch({ type: "APPLY_ROLLS", characters: rolledSorted });
  };

  const nextTurn = () => dispatch({ type: "NEXT_TURN" });

  return (
    <div>
      <div style={{ marginBottom: "10px" }}>
        <button onClick={addCharacter}>➕ Add Character</button>{" "}
        <select
          value={diceSides}
          onChange={(e) =>
            dispatch({ type: "SET_DICE_SIDES", diceSides: Number(e.target.value) })
          }
        >
          {diceOptions.map((s) => (
            <option key={s} value={s}>
              d{s}
            </option>
          ))}
        </select>{" "}
        <button onClick={rollInitiative} disabled={characters.length === 0}>
          🎲 Roll Initiative
        </button>{" "}
        <button onClick={nextTurn} disabled={characters.length === 0}>
          ➡️ Next
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
  {characters.map((c, i) => (
    <div
      key={c.id}
      style={{
        background: i === currentTurnIndex ? "#ffcc00" : "#333",
        color: i === currentTurnIndex ? "#000" : "white",
        fontWeight: i === currentTurnIndex ? "bold" : "normal",
        padding: "6px",
        border: "1px solid #555",
        borderRadius: "4px",
        
      }}
    >
      {/* Row 1: inputs */}
  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
    <input
      type="text"
      value={c.name}
      onChange={(e) => updateCharacter(c.id, "name", e.target.value)}
      placeholder="Name"
      style={{ width: "80px" }}
    />

    <input
      type="number"
      value={c.bonus}
      onChange={(e) => updateCharacter(c.id, "bonus", e.target.value)}
      placeholder="+0"
      style={{ width: "40px" }}
    />
  </div>

      {/* Row 2: initiative left, X right */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginTop: "4px",
      gap: "6px",
    }}
  >
    <div style={{ flex: "1 1 auto" }}>
      {c.roll !== null && <span>Initiative: {c.roll}</span>}
    </div>

    <button
      onClick={() => removeCharacter(c.id)}
      title="Remove"
      aria-label={`Remove ${c.name || "character"}`}
      style={{
        background: "transparent",
        border: "none",
        color: "inherit",
        cursor: "pointer",
        fontSize: "16px",
        lineHeight: 1,
        padding: "2px 6px",
        flex: "0 0 auto",
      }}
    >
      ❌
    </button>
  </div>
</div>
  ))}
</div>

    </div>
  );
}
